/**
 * Notifications pane for portal settings — editable email/digest/threshold
 * preferences persisted via PATCH /portal/settings.
 *
 * Local state updates immediately for a responsive feel; on success we refetch
 * so the server stays the source of truth, and on failure we roll the changed
 * field back to the last loaded value.
 *
 * Preferences are stored PER DOMAIN, so the pane follows the selected domain
 * and says which one it is editing — otherwise a customer with several domains
 * would silently change one domain's alerting while looking at another.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from "@ddmarc/ui";
import { portalApi, type PortalSettings } from "@/lib/portal-api";
import { usePortalScope } from "@/lib/portal-scope-context";
import { toast } from "@/hooks/use-toast";
import { CardV2 } from "@/components/primitives";

// Query key prefix shared by the getSettings read + post-save invalidation.
// The selected domain is part of the key: two domains have different rows.
const SETTINGS_QUERY_KEY = ["portal", "settings"] as const;

export function NotificationsPane() {
  const queryClient = useQueryClient();
  const { actionableDomainId, selectedDomain, hasMultipleDomains } =
    usePortalScope();

  // A null domain_id means "the domain you logged in with" server-side, so a
  // single-domain customer loads normally without waiting on the domain list.
  const { data: settings, isLoading } = useQuery({
    queryKey: [...SETTINGS_QUERY_KEY, actionableDomainId],
    queryFn: () => portalApi.getSettings({ domain_id: actionableDomainId }),
  });

  const [local, setLocal] = useState<PortalSettings>({
    email_notifications: true,
    weekly_digest: true,
    alert_threshold: "high",
  });

  useEffect(() => {
    if (settings) setLocal(settings);
  }, [settings]);

  // Persist a single changed field. Local state updates immediately for a
  // responsive feel; on success we refetch so the server stays the source of
  // truth, and on failure we roll the field back to the last loaded value.
  const { mutate: save, isPending } = useMutation({
    mutationFn: (patch: Partial<PortalSettings>) =>
      portalApi.updateSettings(patch, { domain_id: actionableDomainId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      toast.success({ title: "Settings saved" });
    },
    onError: (err: Error, patch) => {
      // Revert only the field we tried to change.
      if (settings) {
        const key = Object.keys(patch)[0] as keyof PortalSettings;
        setLocal((prev) => ({ ...prev, [key]: settings[key] }));
      }
      toast.error({ title: "Couldn't save settings", description: err.message });
    },
  });

  const update = <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
    save({ [key]: value } as Partial<PortalSettings>);
  };

  // Preferences belong to a single domain, so "All domains" has nothing to
  // edit. Say so rather than writing to an arbitrary domain's row.
  if (hasMultipleDomains && actionableDomainId === null) {
    return (
      <CardV2
        title="Notifications"
        subtitle="Alert preferences are set per domain."
      >
        <p className="text-[12.5px] text-muted-foreground">
          Pick a domain from the selector at the top of the page to change its
          notification settings.
        </p>
      </CardV2>
    );
  }

  return (
    <CardV2
      title="Notifications"
      subtitle={
        selectedDomain
          ? `Choose which ${selectedDomain.domain} alerts reach your inbox.`
          : "Choose which alerts reach your inbox."
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="space-y-5">
          <ToggleRow
            label="Email notifications"
            hint="Receive email alerts for important events."
            checked={local.email_notifications}
            disabled={isPending}
            onCheckedChange={(v) => update("email_notifications", v)}
          />
          <ToggleRow
            label="Weekly digest"
            hint="Get a weekly summary of your domain's DMARC activity by email."
            checked={local.weekly_digest}
            disabled={isPending}
            onCheckedChange={(v) => update("weekly_digest", v)}
          />
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <Label className="text-[12.5px] font-medium text-foreground">
                Alert threshold
              </Label>
              <p className="text-[12px] text-muted-foreground">
                Minimum severity that triggers email notifications.
              </p>
            </div>
            <Select
              value={local.alert_threshold}
              disabled={isPending}
              onValueChange={(v) =>
                update("alert_threshold", v as PortalSettings["alert_threshold"])
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All alerts</SelectItem>
                <SelectItem value="high">High &amp; critical</SelectItem>
                <SelectItem value="critical">Critical only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </CardV2>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onCheckedChange,
  badge,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (v: boolean) => void;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <Label className="text-[12.5px] font-medium text-foreground">
            {label}
          </Label>
          {badge}
        </div>
        <p className="text-[12px] text-muted-foreground">{hint}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}
